import { GridRowId } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { Req_UpdateHotelClinicTasksInBulkPayload } from "@/requests/hotel-clinic-task-update.types";
import { useUpdateHotelClinicTasksMutation } from "@/requests/hotel-clinic.mutations";
import { useHotelClinicArrivalDate } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

/** Applies bulk `PATCH /hotel-clinic/tasks` (status updates, deletes, …). */
function useHotelClinicTasksBulkMutation() {
    const { t } = useTranslation();
    const date = useHotelClinicArrivalDate();
    const isoDate = date.toISODate() ?? undefined;
    const { mutateAsync, isPending } = useUpdateHotelClinicTasksMutation({ isoDate });

    const handleHotelClinicTasksBulkMutation = useCallback(
        async (payload: Req_UpdateHotelClinicTasksInBulkPayload) => {
            if (isPending || !payload.tasks.length) return;
            const isDeleteOnly =
                payload.tasks.length > 0 && payload.tasks.every((task) => task.updateData.deleteTask === true);

            const responses = await mutateAsync(payload);
            const completedList: GridRowId[] = [];

            for (const [index, response] of responses.entries()) {
                if (response.ok) completedList.push(response.id);
                setTimeout(() => {
                    if (!response.ok) {
                        enqueueSnackbar(response.message || "", {
                            variant: "error",
                        });
                    }
                    if (index === responses.length - 1 && completedList.length) {
                        enqueueSnackbar(
                            t(
                                isDeleteOnly
                                    ? "responses.hotelClinicTasksDeleted"
                                    : "responses.hotelClinicTasksStatusUpdated",
                                {
                                    count: completedList.length || 1,
                                }
                            ),
                            {
                                variant: "success",
                            }
                        );
                    }
                }, index * 80);
            }
        },
        [isPending, mutateAsync, t]
    );

    return { handleHotelClinicTasksBulkMutation, isPending };
}

export default useHotelClinicTasksBulkMutation;
